import {createStyles} from '@mantine/core';
import React, {useEffect, useMemo, useState} from 'react';

import {assertAndContinue} from 'Asserts';
import {InsightsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import WorkspaceItemInaccessibleGuard from 'auth/WorkspaceItemInaccessibleGuard';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {useConditionalLocalGraphQLState} from 'graphql-client/useLocalGraphQLState';
import {
  AnalyticsDashboardActions,
  updateAnalyticsDashboard,
} from 'insights/analytics-dashboards/AnalyticsDashboardActions';
import * as MakeSpecRequest from 'insights/analytics-dashboards/data/MakeSpecRequest';
import {UpdateAnalyticsDashboardSpecRequest} from 'insights/analytics-dashboards/RESTAnalyticsDashboardTypes';
import AnalyticsBlockEditor from 'insights/analytics-queries/block-editor/AnalyticsBlockEditor';
import {AnalyticsQuerySpecRequest} from 'insights/analytics-queries/RESTAnalyticsQueryTypes';
import {runAnalyticsQuery} from 'insights/analytics-queries/run-query/RunAnalyticsQueryActions';
import {AnalyticsParameterValue} from 'insights/parameters/__generated/AnalyticsParameterValue';
import {renameParametersInLocalSQLQueryOverrides} from 'insights/parameters/data/RenameParameterConfig';
import {AnalyticsParameterConfigSpecRequest} from 'insights/parameters/RESTParameterTypes';
import WritableContext from 'insights/WritableContext';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {PaneBorder} from 'ui-library/pane/Pane';
import {TabLayoutBackgroundColor} from 'ui-library/TabLayout';
import {withLoadingScreen} from 'util/Promises';

import {AnalyticsParameterConfigGraphQL} from '../../../../graphql-fragments/insights/__generated/AnalyticsParameterConfigGraphQL';
import getTestProps from '../../../test/lib/cypress/getTestProps';
import {useAreAnyVisibleAnalyticsQueriesRunning} from '../data/useAreAnyAnalyticsQueriesRunning';
import AnalyticsDashboardDetailsContext, {
  DEFAULT_DASHBOARD_DETAILS_CONTEXT,
} from './AnalyticsDashboardDetailsContext';
import AnalyticsDashboardEditor from './AnalyticsDashboardEditor';
import deleteAnalyticsQueryFromDashboard from './deleteAnalyticsQueryFromDashboard';
import {
  GetAnalyticsDashboard,
  GetAnalyticsDashboardVariables,
} from './graphql/__generated/GetAnalyticsDashboard';
import {GET_ANALYTICS_DASHBOARD_QUERY} from './graphql/GetAnalyticsDashboard';
import DashboardDetailsParameterPillList from './parameters/DashboardDetailsParameterPillList';
import useRunDashboardQueries from './run-dashboard-queries/useRunDashboardQueries';
import {
  analyticsDashboardGlobalScreenState,
  isBlockEditorScreenState,
  isDashboardEditorScreenState,
  makeBlockEditorScreenState,
  makeDashboardEditorScreenState,
  ScreenState,
} from './view-state/ScreenState';

type AnalyticsDashboard = ExtractByTypename<
  GetAnalyticsDashboard['analyticsDashboard'],
  'AnalyticsDashboardNode'
>;

export type Actions = Pick<AnalyticsDashboardActions, 'updateAnalyticsDashboard'> & {
  openBlockEditor: (analyticsQueryId: string) => void;
  openDashboardEditor: () => void;
  runAnalyticsQuery: typeof runAnalyticsQuery;
};

interface Props {
  actions: Actions;
  // A dashboard is usually determined to be writable or not depending on its permissions, which are defined
  // in serverAnalyticsDashboard. The readOnly prop overrides that check and forces a dashboard to always
  // be not-writable.
  readOnly: boolean;
  delayExpensiveRenders: boolean;
  showReadOnlyBadge: boolean;
  showDashboardCloneButton: boolean;
  showDashboardLink: boolean;
  theme: {
    dashboardBackgroundColor: TabLayoutBackgroundColor;
    paneBorder: PaneBorder;
  };
  screenState: ScreenState;
  serverAnalyticsDashboard: AnalyticsDashboard;
  viewStateId: string;
  // parameterValueOverrides allows callers to specify a list of parameterValues to use
  // instead of serverAnalyticsDashboard.parameterValues. The overrides are only applied once, on mount.
  //
  // The overrides will only be used if the dashboard is read-only; otherwise, server's parameterValues will
  // always be used.
  //
  // If specified, parameters not included in `parameterValueOverrides` will be set to null.
  // Specified overrides without a corresponding config in the dashboard are ignored.
  parameterValueOverrides?: Array<AnalyticsParameterValue>;
  // The _skipMalformedStateAssertion prop allows callers to skip the malformed state assertion. This is useful
  // for tests that want to test how well AnalyticsDashboardDetails behaves with a malformed state.
  _skipMalformedStateAssertion?: boolean;
}

function useCheckScreenState({
  openDashboardEditor,
  screenState,
  skipAssert,
  writable,
}: {
  openDashboardEditor: Actions['openDashboardEditor'];
  screenState: ScreenState;
  skipAssert: boolean;
  writable: boolean;
}): boolean {
  const isScreenStateValid =
    isDashboardEditorScreenState(screenState) || (isBlockEditorScreenState(screenState) && writable);

  useEffect(() => {
    if (!isScreenStateValid) {
      if (!skipAssert) {
        assertAndContinue(
          false,
          'Expected dashboard editor or writable block editor screen, got but unexpected screenState',
          {screenState, writable}
        );
      }
      // In the case we get a bad state, we need to reset the state to default in order to fail gracefully.
      openDashboardEditor();
    }
  }, [isScreenStateValid, openDashboardEditor, screenState, skipAssert, writable]);

  return isScreenStateValid;
}

/** @package */
export const localParameterValueOverridesState =
  makeUseGlobalClientState<Array<AnalyticsParameterValue> | null>(null);

const useStyles = createStyles(() => ({
  root: {
    height: '100%',
  },
}));

function AnalyticsDashboardDetails(props: Props): JSX.Element | null {
  const {classes} = useStyles();
  const {
    screenState,
    serverAnalyticsDashboard: {analyticsQueries, parameterConfigs},
    viewStateId,
    _skipMalformedStateAssertion = false,
  } = props;
  const writable = props.serverAnalyticsDashboard.permissions.writable && !props.readOnly;
  const analyticsDashboardId = props.serverAnalyticsDashboard.id;

  // There is no draft state for parameterValues in writable dashboards, so we can't use
  // parameterValueOverrides unless we either implement such a draft state or save the values to the server.
  // We don't expect this prop to be used outside of a read-only context for the time being, so we simply ignore
  // the prop if used in a writable context.
  assertAndContinue(
    props.parameterValueOverrides == null || !writable,
    'Expected to only use the parameterValueOverrides prop when the dashboard details are read-only'
  );

  // We don't want to be able to change parameter value overrides throughout the life of the component by
  // changing the props, so we store the prop's value on the first render.
  const [originalParameterValueOverrides] = useState(writable ? undefined : props.parameterValueOverrides);
  const [localParameterValueOverrides, setLocalParameterValueOverrides] =
    localParameterValueOverridesState.useGlobalClientState(viewStateId);

  const isScreenStateValid = useCheckScreenState({
    openDashboardEditor: props.actions.openDashboardEditor,
    screenState,
    skipAssert: _skipMalformedStateAssertion,
    writable,
  });

  async function updateWithLoadingScreen(data: UpdateAnalyticsDashboardSpecRequest): Promise<void> {
    // If we see this error, then that means we didn't thoroughly hide or disable all write actions in the
    // read-only UI.
    // We're OK from a permissions standpoint, because the API checks that the user has write access to the
    // dashboard. The user will see a toast that they lack permissions.
    // But we should update the UI to prevent them from taking the write action to begin with.
    assertAndContinue(writable, 'Unexpectedly trying to update dashboard as read-only user');
    runOnUpdateDashboard(data);
    await withLoadingScreen(
      props.actions.updateAnalyticsDashboard(analyticsDashboardId, data),
      'Saving dashboard...'
    );
  }
  async function updateAnalyticsQuery(
    analyticsQueryId: string,
    newAnalyticsQuerySpecRequest: AnalyticsQuerySpecRequest,
    additionalParameterConfigs?: Array<AnalyticsParameterConfigSpecRequest>
  ): Promise<void> {
    await updateWithLoadingScreen(
      additionalParameterConfigs
        ? MakeSpecRequest.onChangeAnalyticsQueryAndParams(
            props.serverAnalyticsDashboard,
            analyticsQueryId,
            newAnalyticsQuerySpecRequest,
            additionalParameterConfigs
          )
        : MakeSpecRequest.onChangeAnalyticsQuery(
            props.serverAnalyticsDashboard,
            analyticsQueryId,
            newAnalyticsQuerySpecRequest
          )
    );
  }
  function handleDeleteAnalyticsQuery(analyticsQueryId: string): void {
    deleteAnalyticsQueryFromDashboard(props.serverAnalyticsDashboard, analyticsQueryId, {
      openDashboardEditor: handleOpenDashboardEditor,
      updateAnalyticsDashboard: props.actions.updateAnalyticsDashboard,
    });
  }
  function handleChangeParameterConfigs(newParameterConfigs: Array<AnalyticsParameterConfigGraphQL>): void {
    updateWithLoadingScreen(
      MakeSpecRequest.onChangeParameterConfigs(props.serverAnalyticsDashboard, newParameterConfigs)
    );

    // If parameters were renamed, then rename them across the local SQL queries
    renameParametersInLocalSQLQueryOverrides(analyticsQueries, parameterConfigs, newParameterConfigs);
  }

  // The only supported dashboard-level local attribute is parameter values, because a user can change them
  // in read-only mode. We want to save their changes locally, but not persist them to the server.
  //
  // This is notably different from other attributes, which we don't allow changing in read-only mode.
  const [parameterValues, handleChangeParameterValuesWithoutRun] = useConditionalLocalGraphQLState(
    !writable,
    viewStateId,
    localParameterValueOverrides,
    (_, newLocalParameterValuesOverrides) => {
      setLocalParameterValueOverrides(newLocalParameterValuesOverrides);
    },
    originalParameterValueOverrides === undefined
      ? props.serverAnalyticsDashboard.parameterValues
      : originalParameterValueOverrides,
    (_, newParameterValues) => {
      updateWithLoadingScreen(
        MakeSpecRequest.onChangeParameterValues(props.serverAnalyticsDashboard, newParameterValues)
      );
    }
  );
  function handleChangeParameterValues(newParameterValues: Array<AnalyticsParameterValue>): void {
    runOnChangeParameterValues(newParameterValues);
    handleChangeParameterValuesWithoutRun(newParameterValues);
  }

  function handleChangeParameterConfigsAndValues(
    newParameterConfigs: Array<AnalyticsParameterConfigGraphQL>,
    newParameterValues: Array<AnalyticsParameterValue>
  ): void {
    updateWithLoadingScreen(
      MakeSpecRequest.onChangeParameterConfigsAndValues(props.serverAnalyticsDashboard, {
        configs: newParameterConfigs,
        values: newParameterValues,
      })
    );

    // If parameters were renamed, then rename them across the local SQL queries
    renameParametersInLocalSQLQueryOverrides(analyticsQueries, parameterConfigs, newParameterConfigs);
  }

  // Server dashboard with local parameter values. Pass to child components so that they access the correct
  // parameter values.
  const localAnalyticsDashboard = useMemo(
    () => ({...props.serverAnalyticsDashboard, parameterValues}),
    [parameterValues, props.serverAnalyticsDashboard]
  );
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const {runOnChangeParameterValues, runOnUpdateDashboard} = useRunDashboardQueries({
    actions: props.actions,
    analyticsDashboard: localAnalyticsDashboard,
    analyticsDashboardViewStateId: viewStateId,
  });

  function handleOpenBlockEditor(analyticsQueryId: string): void {
    props.actions.openBlockEditor(analyticsQueryId);
  }

  function handleOpenDashboardEditor(): void {
    props.actions.openDashboardEditor();
  }
  const areQueriesRunning = useAreAnyVisibleAnalyticsQueriesRunning(
    props.serverAnalyticsDashboard,
    viewStateId,
    screenState
  );
  const parameterList =
    parameterConfigs.length > 0 ? (
      <DashboardDetailsParameterPillList
        areQueriesRunning={areQueriesRunning}
        onChangeParameterConfigs={handleChangeParameterConfigs}
        onChangeParameterValues={handleChangeParameterValues}
        parameterConfigs={parameterConfigs}
        parameterValues={parameterValues}
      />
    ) : null;

  let editor;

  if (!isScreenStateValid) {
    return null;
  } else if (isBlockEditorScreenState(screenState)) {
    // screenState.analyticsQuery.id cannot be called inside the find() function since typescript doesn't know
    // if the screenState type will remain the same during the operation, so we instead define a separate
    // function call it with screenState.analyticsQuery.id to get the query, which is fine because it
    // guarantees that analyticsQueryId will exist while the find() function executes.
    const findQuery = (analyticsQueryId: string): AnalyticsDashboard['analyticsQueries'][number] =>
      analyticsQueries.find((query) => query.id === analyticsQueryId)!;

    editor = (
      <AnalyticsBlockEditor
        actions={{
          ...props.actions,
          deleteAnalyticsQuery: handleDeleteAnalyticsQuery,
          updateAnalyticsQuery,
          openDashboardEditor: handleOpenDashboardEditor,
        }}
        analyticsDashboardAttrs={localAnalyticsDashboard}
        analyticsQuery={findQuery(screenState.analyticsQuery.id)}
        parameterList={parameterList}
        parameterConfigs={parameterConfigs}
        parameterValues={parameterValues}
      />
    );
  } else {
    assertAndContinue(isDashboardEditorScreenState(screenState), `Unexpected screenStateMode`, {screenState});
    editor = (
      <AnalyticsDashboardEditor
        actions={{
          ...props.actions,
          deleteAnalyticsQuery: handleDeleteAnalyticsQuery,
          openBlockEditor: handleOpenBlockEditor,
          updateParameterConfigsAndValues: handleChangeParameterConfigsAndValues,
          updateParameterValues: handleChangeParameterValues,
        }}
        analyticsDashboard={localAnalyticsDashboard}
        parameterList={parameterList}
      />
    );
  }

  return (
    <div
      className={classes.root}
      {...getTestProps({component: 'AnalyticsDashboardDetails', element: 'root'})}
    >
      <AnalyticsDashboardDetailsContext.Provider
        // eslint-disable-next-line react/jsx-no-constructed-context-values -- Disabling for now, but worth fixing
        value={{
          ...DEFAULT_DASHBOARD_DETAILS_CONTEXT,
          analyticsDashboardViewStateId: viewStateId,
          delayExpensiveRenders: props.delayExpensiveRenders,
          showReadOnlyBadge: props.showReadOnlyBadge,
          showDashboardLink: props.showDashboardLink,
          showDashboardCloneButton:
            props.showDashboardCloneButton && isFeatureEnabled({feature: InsightsFeature.INSIGHTS_CREATE}),
          theme: props.theme,
        }}
      >
        <WritableContext.Provider value={writable}>{editor}</WritableContext.Provider>
      </AnalyticsDashboardDetailsContext.Provider>
    </div>
  );
}

export type QueryingAnalyticsDashboardProps = Omit<Props, 'serverAnalyticsDashboard'> & {
  analyticsDashboardId: string;
};

export function QueryingAnalyticsDashboardDetails({
  analyticsDashboardId,
  ...childProps
}: QueryingAnalyticsDashboardProps): JSX.Element {
  return (
    <BenchlingQuery<GetAnalyticsDashboard, GetAnalyticsDashboardVariables>
      query={GET_ANALYTICS_DASHBOARD_QUERY}
      renderLoading={() => <CenteredHorizontalThrobber />}
      variables={{
        analyticsDashboardId,
      }}
    >
      {(data) =>
        isInaccessible(data.analyticsDashboard) ? (
          <WorkspaceItemInaccessibleGuard
            inaccessibleInfo={data.analyticsDashboard}
            modelType={ModelTypeConstants.ANALYTICS_DASHBOARD}
          />
        ) : (
          <AnalyticsDashboardDetails {...childProps} serverAnalyticsDashboard={data.analyticsDashboard} />
        )
      }
    </BenchlingQuery>
  );
}

/** @package */
export type ConnectedAnalyticsDashboardDetailsProps = Omit<
  QueryingAnalyticsDashboardProps,
  'actions' | 'screenState'
>;

export default function ConnectedAnalyticsDashboardDetails(
  props: ConnectedAnalyticsDashboardDetailsProps
): JSX.Element {
  const [screenState, setScreenState] = analyticsDashboardGlobalScreenState.useGlobalClientState(
    props.viewStateId
  );

  return (
    <QueryingAnalyticsDashboardDetails
      {...props}
      actions={{
        openBlockEditor: (analyticsQueryId: string) => {
          setScreenState(makeBlockEditorScreenState(analyticsQueryId));
        },
        openDashboardEditor: () => {
          setScreenState(makeDashboardEditorScreenState());
        },
        runAnalyticsQuery,
        updateAnalyticsDashboard,
      }}
      screenState={screenState}
    />
  );
}

ConnectedAnalyticsDashboardDetails.defaultProps = {
  readOnly: false,
};
